import { responsiveImageProps } from '../../lib/publicMedia';
import { usePublicPageData } from './PublicPageData';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import axios from 'axios';
import { foregroundFor, paletteFor, siteBackgroundFor } from '../../cms/siteCmsPalettes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fallbackItems = [
  { label: 'Home', href: '/', order: 0 },
  { label: 'IV Therapy', href: '/iv-supplements-hull', order: 1 },
  { label: 'Rehabilitation', href: '/physiotherapy', order: 2 },
  { label: 'Blood Tests', href: '/blood-test', order: 3 },
  { label: 'Training', href: '/training-hull', order: 4 },
  { label: 'Blog', href: '/blog', order: 20 },
];

let NAV_CACHE = fallbackItems;
let NAV_LOADED = false;
let navigationRequest = null;
const PREFETCHED_HREFS = new Set();

const loadNavigation = () => {
  if (NAV_LOADED) return Promise.resolve(NAV_CACHE);
  if (!navigationRequest) {
    navigationRequest = Promise.all([
      axios.get(`${BACKEND_URL}/api/v2/public/navigation`),
      axios.get(`${BACKEND_URL}/api/v2/public/nav-extras`),
    ]).then(([pagesResponse, extrasResponse]) => {
      const dynamic = pagesResponse.data?.items || [];
      const extras = extrasResponse.data?.items || [];
      const unique = [];
      const seen = new Set();
      [...dynamic, ...extras].slice().sort((a, b) => Number(a.order || 0) - Number(b.order || 0)).forEach(item => {
        if (!item?.href || seen.has(item.href)) return;
        seen.add(item.href);
        unique.push(item);
      });
      NAV_CACHE = unique.length ? unique : fallbackItems;
      NAV_LOADED = true;
      return NAV_CACHE;
    }).catch(() => NAV_CACHE).finally(() => { navigationRequest = null; });
  }
  return navigationRequest;
};

const prefetchPublicHref = href => {
  if (!BACKEND_URL || !href || PREFETCHED_HREFS.has(href) || href.startsWith('/blog') || href.startsWith('/booking') || href.includes('#')) return;
  const path = String(href).split('?')[0].replace(/^\/+|\/+$/g, '');
  const url = path ? `${BACKEND_URL}/api/v2/public/page/${path.split('/').filter(Boolean).map(encodeURIComponent).join('/')}` : `${BACKEND_URL}/api/v2/public/home`;
  PREFETCHED_HREFS.add(href);
  fetch(url, { method: 'GET', cache: 'default' }).catch(() => PREFETCHED_HREFS.delete(href));
};

const PublicHeaderV2 = ({ style }) => {
  const initial = usePublicPageData();
  if (typeof window !== 'undefined' && initial?.navigation && !NAV_LOADED) {
    NAV_CACHE = initial.navigation;
    NAV_LOADED = true;
  }
  const [items, setItems] = useState(initial?.navigation || NAV_CACHE);
  const [open, setOpen] = useState(false);
  const visual = useMemo(() => {
    if (!style) return { background: '#1a0e06', foreground: '#FFFFFF' };
    const palette = paletteFor(style);
    const background = siteBackgroundFor(style);
    return { background, foreground: foregroundFor(palette, background) };
  }, [style]);

  useEffect(() => {
    let active = true;
    loadNavigation().then(next => { if (active) setItems(next); });
    return () => { active = false; };
  }, []);

  const goHome = () => {
    setOpen(false);
    if (window.location.pathname === '/') window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const borderColor = `color-mix(in srgb, ${visual.foreground} 18%, transparent)`;
  const subtleBackground = `color-mix(in srgb, ${visual.foreground} 8%, transparent)`;

  return <header data-nx-navbar className="sticky top-0 z-50 border-b backdrop-blur" style={{ backgroundColor: visual.background, color: visual.foreground, borderColor }}>
    <div data-nx-navbar-inner className="max-w-7xl mx-auto h-20 px-4 sm:px-6 flex items-center justify-between gap-5">
      <Link to="/" onPointerEnter={() => prefetchPublicHref('/')} onFocus={() => prefetchPublicHref('/')} onClick={goHome} className="flex items-center gap-3 flex-shrink-0" style={{ color: visual.foreground }} aria-label="Just Vitality Clinic - Home"><img src="/images/logo.jpeg" {...responsiveImageProps('/images/logo.jpeg', '40px')} alt="Just Vitality Clinic" width="40" height="40" className="w-10 h-10 rounded-xl object-cover" /><div className="font-semibold whitespace-nowrap"><span>Just Vitality</span> Clinic</div></Link>
      <nav className="hidden lg:flex items-center gap-5 text-sm">{items.map(item => <Link data-nx-nav-link key={`${item.href}-${item.label}`} to={item.href} onPointerEnter={() => prefetchPublicHref(item.href)} onFocus={() => prefetchPublicHref(item.href)} onClick={item.href === '/' ? goHome : undefined} className="transition-opacity opacity-75 hover:opacity-100" style={{ color: visual.foreground }}>{item.label}</Link>)}</nav>
      <div className="flex items-center gap-2"><button data-testid="mobile-menu-open" onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-lg border" style={{ color: visual.foreground, borderColor }} aria-label="Menu">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button></div>
    </div>
    {open && <nav data-nx-mobile-menu="true" className="lg:hidden border-t px-4 sm:px-6 py-4 flex flex-col gap-1" style={{ backgroundColor: visual.background, color: visual.foreground, borderColor }}>{items.map(item => <Link data-nx-nav-link key={`${item.href}-${item.label}`} to={item.href} onPointerEnter={() => prefetchPublicHref(item.href)} onFocus={() => prefetchPublicHref(item.href)} onClick={() => { setOpen(false); if (item.href === '/' && window.location.pathname === '/') window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' })); }} className="px-3 py-3 rounded-lg transition-opacity opacity-80 hover:opacity-100" style={{ color: visual.foreground, '--nx-nav-hover-bg': subtleBackground }}>{item.label}</Link>)}</nav>}
  </header>;
};

export default PublicHeaderV2;
